import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import MediaUpload from "../../components/Forms/MediaUpload";
import RichTextEditor from "../../components/Forms/RichTextEditor";
import { ActionButton } from "../../components/Forms/ActionButton";
import { useCreateTip, useGetTipById, useUpdateTip } from "../../queries/knowledgeBoard";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateBadge, useGetBadgeById, useGetBadgeCategoryList, useUpdateBadge } from "../../queries/badge";

const CreateBadge = () => {

  const navigate = useNavigate();

  const { state } = useLocation();
  const id = state?.id;

  const [active, setActive] = useState(true);
  const [coverImage, setCoverImage] = useState(null);
  const [mediaClear, setMediaClear] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const { data, isLoading } = useGetBadgeById(id);

  const { data: categoryList, isLoading: isCategoryListPending } = useGetBadgeCategoryList();

  const { mutateAsync: create, isPending: isCreating } = useCreateBadge();

  const { mutateAsync: update, isPending: isUpdating } = useUpdateBadge();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      name: data ? data.name : "",
      description: data ? data.description : "",
      minPoints: data ? data.minPoints : "",
      minCarbonKg: data ? data.minCarbonKg : "",
      minTrees: data ? data.minTrees : "",
      minFriendsInvited: data ? data.minFriendsInvited : "",
      minDonations: data ? data.minDonations : "",
      minChallengesCompleted: data ? data.minChallengesCompleted : "",
      minEventsAttended: data ? data.minEventsAttended : "",
      colorScheme: data ? data.colorScheme : "",
    }
  });

  const onSubmit = async (data) => {
    if (!coverImage) {
      alert("Please upload a thumbnail.");
      return;
    }
    if (getValues('description') === "") {
      alert("Please write a description.");
      return;
    }
    console.log({
      id: id,
      name: data.name,
      category: data.category,
      description: data.description,
      minPoints: data.minPoints || 0,
      minCarbonKg: data.minCarbonKg || 0,
      minTrees: data.minTrees || 0,
      minFriendsInvited: data.minFriendsInvited || 0,
      minDonations: data.minDonations || 0,
      minChallengesCompleted: data.minChallengesCompleted || 0,
      minEventsAttended: data.minEventsAttended || 0,
      colorScheme: data.colorScheme,
      isActive: active,
      file: coverImage || "",
    });
    if (id) {
      toast.loading('Updating Badge...');
      await update({
        id: id,
        name: data.name,
        category: data.category,
        description: data.description,
        minCarbonKg: data.minCarbonKg || 0,
        minPoints: data.minPoints || 0,
        minTrees: data.minTrees || 0,
        minFriendsInvited: data.minFriendsInvited || 0,
        minDonations: data.minDonations || 0,
        minChallengesCompleted: data.minChallengesCompleted || 0,
        minEventsAttended: data.minEventsAttended || 0,
        colorScheme: data.colorScheme,
        isActive: active,
        file: coverImage || "",
      }).then((res) => {
        if (res?.error) {
          toast.dismiss();
          console.error("Error creating badge:");
          return;
        }
        toast.dismiss();
        toast.success("Badge updated successfully!");
        setTimeout(() => {
          navigate('/badge/badge-main/view-badges');
        }, 2000);
      }).catch((error) => {
        toast.dismiss();
        console.error("Error updating badge:", error);
        toast.error("Error updating badge. Please try again.", error?.res?.data?.error);
      });
    } else {
      toast.loading('Creating Badge...');
      await create({
        name: data.name,
        category: data.category,
        description: data.description,
        minPoints: data.minPoints || 0,
        minCarbonKg: data.minCarbonKg || 0,
        minTrees: data.minTrees || 0,
        minFriendsInvited: data.minFriendsInvited || 0,
        minDonations: data.minDonations || 0,
        minChallengesCompleted: data.minChallengesCompleted || 0,
        minEventsAttended: data.minEventsAttended || 0,
        colorScheme: data.colorScheme,
        isActive: active,
        file: coverImage || "",
      }).then((res) => {
        if (res?.error) {
          toast.dismiss();
          console.error("Error creating badge:");
          return;
        }
        toast.dismiss();
        toast.success("Badge created successfully!");
        setTimeout(() => {
          navigate('/badge/badge-main/view-badges');
        }, 2000);
      }).catch((error) => {
        toast.dismiss();
        console.error("Error creating badge:", error);
        toast.error("Error creating badge. Please try again.", error?.res?.data?.error);
      });
    }
  }

  useEffect(() => {
    setMediaClear(false);
  }, [coverImage]);

  // Function to handle category change
  const handleCategoryChange = (categoryId) => {
    if (!categoryId) {
      setSelectedCategory(null);
      return;
    }

    const category = categoryList.find(cat => cat._id === categoryId);
    setSelectedCategory(category);
  };

  useEffect(() => {
    if (data && categoryList) {
      setValue("name", data.name);
      setValue("description", data.description || "");
      setValue("minPoints", data.minPoints);
      setValue("minCarbonKg", data.minCarbonKg);
      setValue("minTrees", data.minTrees);
      setValue("minFriendsInvited", data.minFriendsInvited);
      setValue("minDonations", data.minDonations);
      setValue("minChallengesCompleted", data.minChallengesCompleted);
      setValue("minEventsAttended", data.minEventsAttended);
      setValue("colorScheme", data.colorScheme);
      setValue("category", data.category._id);
      setActive(data.isActive);
      setCoverImage(data.imageUrl);

      // Set selected category
      const categoryData = categoryList.find(cat => cat._id === data.category);
      setSelectedCategory(categoryData);
    }
  }, [data, categoryList, setValue]);

  useEffect(() => {
    setTimeout(() => {
      if (data) {
        setCoverImage(data.imageUrl);
        setValue("description", data.description || "");
        handleCategoryChange(data.category._id);
      }
    }, 10);
  }, [data]);

  if (isLoading || isCategoryListPending) {
    return <div className="flex justify-center items-center h-screen">
      <p className="text-lg font-semibold">Loading...</p>
    </div>
  }

  return (
    <form className="flex flex-col w-full font-semibold" onSubmit={handleSubmit(onSubmit)}>
      <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3  w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
        <div className="flex items-center justify-between">
          <p className="text-base font-bold mt-3">Badge Information</p>
          <div className="flex gap-3 items-center">
            <p className="text-base">Status:</p>
            <div className={`flex justify-end items-center border ${active ? `border-primary` : `border-inactive`} rounded-md h-full min-h-8 min-w-20`}>
              <label className="inline-flex items-center me-5 cursor-pointer h-full">
                <span className={`ms-3 text-base ${active ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{active ? 'Active' : 'Inactive'}</span>
                <input type="checkbox" name={'active'} value={active} className="sr-only peer" onChange={() => setActive(!active)} checked={active} />
                <div className="relative w-8 h-4 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.1 after:start-[0px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex flex-col w-full gap-4">
            {/* Name */}
            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Badge Name</label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <input
                  type="text"
                  placeholder="Badge Name"
                  {...register("name", { required: "name is required" })}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.name ? "border-red-500" : ""}`}
                />
              </div>
              {errors.name && <p className="text-red text-sm">{errors.name.message}</p>}
            </div>
            {/* Category */}
            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
              <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Badge Category</label>
              <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                <select
                  {...register("category")}
                  className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.category ? "border-red-500" : ""}`}
                  onChange={(e) => {
                    handleCategoryChange(e.target.value);
                    // Make sure react-hook-form also gets the value
                    register("category").onChange(e);
                  }}
                >
                  <option value="">Select Category</option>
                  {categoryList.map((category) => {
                    if(category?.isActive){
                      return (<option key={category?._id} value={category?._id}>
                        {category?.name}
                      </option>)
                    }
                  }
                  )}
                </select>
              </div>
              {errors.category && <p className="text-red text-sm">{errors.category.message}</p>}
            </div>
            {/* Description */}
            <div className="mt-2">
              <RichTextEditor
                key={data?.id || "new"}
                label="Description"
                placeholder="Write your overview here..."
                name="description"
                value={getValues("description") || ""}
                onChange={(value) => setValue("description", value)}
                error={errors.description}
              />
              {errors.source && <p className="text-red text-sm">{errors.source.message}</p>}
            </div>
          </div>
          {/* Cover Image */}
          <div className="mt-0">
            <MediaUpload
              key={coverImage || "new"}
              clearMedia={mediaClear}
              label={"Thumbnail"}
              existingImage={coverImage}
              setImageUpload={setCoverImage}
              stateImage={coverImage}
              setStateImageUpload={setCoverImage}
            />
          </div>
        </div>

        {/* Minimum Points */}
        {(!selectedCategory || selectedCategory?.points) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter The Minimum Number Of Points</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minPoints", {
                  required: selectedCategory?.points ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Points cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Points cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minPoints ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minPoints && <p className="text-red text-sm">{errors.minPoints.message}</p>}
          </div>
        )}

        {/* Minimum donations */}
        {(!selectedCategory || selectedCategory?.donations) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter The Minimum Donations</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minDonations", {
                  required: selectedCategory?.donations ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Donations cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Donations cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minDonations ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minDonations && <p className="text-red text-sm">{errors.minDonations.message}</p>}
          </div>
        )}

        {/* Minimum carbon saving */}
        {(!selectedCategory || selectedCategory?.carbon) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter Minimum Number Of Carbons In KG</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minCarbonKg", {
                  required: selectedCategory?.carbon ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Carbon Savings cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Carbon Savings cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minCarbonKg ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minCarbonKg && <p className="text-red text-sm">{errors.minCarbonKg.message}</p>}
          </div>
        )}

        {/* Minimum challenges complete */}
        {(!selectedCategory || selectedCategory?.challenges) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter The Minimum No Of Challenges Completed</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minChallengesCompleted", {
                  required: selectedCategory?.challenges ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Number of challenges cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Number of challenges cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minChallengesCompleted ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minChallengesCompleted && <p className="text-red text-sm">{errors.minChallengesCompleted.message}</p>}
          </div>
        )}

        {/* Minimum Enter The Minimum No Of Trees */}
        {(!selectedCategory || selectedCategory?.trees) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter The Minimum No Of Trees</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minTrees", {
                  required: selectedCategory?.trees ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Trees cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Trees cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minTrees ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minTrees && <p className="text-red text-sm">{errors.minTrees.message}</p>}
          </div>
        )}

        {/* Minimum Events Attended */}
        {(!selectedCategory || selectedCategory?.events) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter The Minimum No Of Events Attended</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minEventsAttended", {
                  required: selectedCategory?.events ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Events cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Events cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minEventsAttended ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minEventsAttended && <p className="text-red text-sm">{errors.minEventsAttended.message}</p>}
          </div>
        )}

        {/* Enter Minimum Friends Invited */}
        {(!selectedCategory || selectedCategory?.friends) && (
          <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Enter Minimum Friends Invited</label>
            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
              <input
                type="number"
                placeholder="Eg. 10"
                {...register("minFriendsInvited", {
                  required: selectedCategory?.friends ? "This field is required" : false,
                  min: {
                    value: 0,
                    message: "Number of friends cannot be negative"
                  },
                  max: {
                    value: 10000000,
                    message: "Number of friends cannot exceed 10,000,000"
                  },
                  valueAsNumber: true
                })}
                className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.minFriendsInvited ? "border-red-500" : ""}`}
              />
            </div>
            {errors.minFriendsInvited && <p className="text-red text-sm">{errors.minFriendsInvited.message}</p>}
          </div>
        )}

        {/* Color Scheme for Badges - Always show this regardless of category */}
        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
          <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Color Scheme for Badges</label>
          <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
            <input
              type="color"
              placeholder="Eg. 10"
              {...register("colorScheme", { required: "This field is required" })}
              className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-3 rounded min-h-10 rounded-md text-base ${errors.colorScheme ? "border-red-500" : ""}`}
            />
          </div>
          {errors.colorScheme && <p className="text-red text-sm">{errors.colorScheme.message}</p>}
        </div>

        <div className="mt-10 flex gap-5 items-center justify-between">
          <ActionButton
            variant="reset"
            text="Reset"
            handleSubmit={() => {
              setCoverImage(null);
              setMediaClear(true);
              reset({
                colorScheme: "",
                name: "",
                description: "",
                minPoints: "",
                minCarbonKg: "",
                minTrees: "",
                minFriendsInvited: "",
                minDonations: "",
                minChallengesCompleted: "",
                minEventsAttended: "",
                category: "",
              });
              setActive(true);
            }}
          />
          <div className="flex items-center gap-3">
            <ActionButton
              variant="cancel"
              text="Cancel"
              handleSubmit={() => navigate('/badge/badge-main/view-badges')}
            />
            <ActionButton
              variant="submit"
              text="Save"
              handleSubmit={() => { }}
              disabled={isCreating || isUpdating}
            />
          </div>
        </div>
      </section>
      <ToastContainer
        position="top-right"
      />
    </form>
  );
}

export default CreateBadge;